import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {catchError, map, Observable, of, shareReplay, switchMap, take, throwError} from 'rxjs';
import {environment} from 'src/environments/environment';
import {ProfileService} from '../../../auth_profile/services/profile.service';
import {CacheService} from '../../../utils/services/cache.service';
import {Page} from '../../../utils/pageable';
import {
  V3DrmProduct,
  V3ProductShare,
  V3ShareDownload,
} from '../../model/v3/drm';
import {BookReaderApiV3Service} from './book-reader-api-v3.service';

@Injectable({
  providedIn: 'root'
})
export class AppRestV3Service {

  private drmAccountId$: Observable<number> = null

  constructor(
    private http: HttpClient,
    private cache: CacheService,
    private profileService: ProfileService,
    private bookReaderApi: BookReaderApiV3Service) {}

  private productsUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/products${path}`;
  }

  private accountsUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/accounts${path}`;
  }

  /**
   * DRM account id of the logged-in person (resolved from the CASA account id, cached).
   */
  private getDrmAccountId(): Observable<number> {
    if (!this.drmAccountId$) {
      this.drmAccountId$ = this.profileService.getSelfPerson().pipe(
        take(1),
        switchMap(person => person?.accountId != null
          ? this.bookReaderApi.getAccountForCasaAccount(person.accountId)
          : throwError(() => new Error('logged-in person has no CASA account id'))),
        switchMap(account => account
          ? of(account.id)
          : throwError(() => new Error('no DRM account linked to the CASA account'))),
        shareReplay(1),
        catchError(err => {
          // do not cache a failed resolution
          this.drmAccountId$ = null
          return throwError(() => err)
        })
      )
    }
    return this.drmAccountId$
  }

  /**
   * Product shares (entitlements) of the DRM account, optionally filtered by product.
   * Each share embeds its `product`, so no separate catalog read is needed for owned books.
   */
  getShares(productId?: number): Observable<Page<V3ProductShare>> {
    let params = new HttpParams().set('size', '200')
    if (productId != null) params = params.set('productId', productId)
    return this.cache.cache(`books/shares-v3/${productId ?? 'all'}`, () =>
      this.getDrmAccountId().pipe(
        switchMap(accountId => this.http.get<Page<V3ProductShare>>(this.accountsUrl(`/${accountId}/shares`), {params}))
      )
    );
  }

  /**
   * DRM product catalog, optionally filtered by identifiers
   * (e.g. for products not owned by the account or for `displayPublic` listing).
   */
  getProducts(ids?: number[]): Observable<Page<V3DrmProduct>> {
    let params = new HttpParams().set('size', '200')
    ids?.forEach(id => params = params.append('id', id))
    return this.http.get<Page<V3DrmProduct>>(this.productsUrl(), {params})
  }

  /**
   * Registered downloads of a share (device slots in use).
   */
  getDownloads(shareId: number): Observable<V3ShareDownload[]> {
    return this.getDrmAccountId().pipe(
      switchMap(accountId => this.http.get<V3ShareDownload[]>(this.accountsUrl(`/${accountId}/shares/${shareId}/downloads`)))
    )
  }

  /**
   * Cover image of the product (current version). v3 cover endpoints require authorization,
   * so the image is fetched through the http client and exposed as a local object URL -
   * the returned value can be bound to `img src` exactly like the legacy public URL.
   */
  getCoverObjectUrl(productId: number): Observable<string> {
    return this.cache.cache(`books/cover-v3/${productId}`, () =>
      this.http.get(this.productsUrl(`/${productId}/cover`), {responseType: 'blob'}).pipe(
        map(blob => URL.createObjectURL(blob)),
        this.map404ToNull<string>()
      ), 3600000);
  }

  private map404ToNull<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null as T);
          }
          return throwError(() => error);
        })
      );
  }
}
